import { datajs, isBreakpointMdUp } from '../shared/utils';
import Plyr from 'plyr';

export default class HomePageVideo {
	static init() {
		const homeVideoScrollIndicator = datajs('home-video-scroll-indicator');
		const homePageVideoElement = datajs('home-page-video');
		let ticking = false;

		if (!homeVideoScrollIndicator || !homePageVideoElement) {
			return;
		}

		this.setupHomePageVideo(homePageVideoElement);

		const scrollToVideoBlock = (e) => {
			e.preventDefault();
			homePageVideoElement.scrollIntoView();
		};

		const onIntersection = (entries) => {
			let entry = entries[0];

			if (!ticking) {
				window.requestAnimationFrame(() => {
					if (entry?.intersectionRatio >= 0.75) {
						HomePageVideo.triggerVideoPlayStateChange();
						homeVideoScrollIndicator.classList.add('is-hidden');
						homePageVideoElement.classList.add('u-angles-none');
					} else {
						HomePageVideo.pauseVideos();
						homeVideoScrollIndicator.classList.remove('is-hidden');
						homePageVideoElement.classList.remove('u-angles-none');
					}
					ticking = false;
				});

				ticking = true;
			}
		};

		const intersectionObserver = new IntersectionObserver(onIntersection, {
			threshold: [0.75],
		});

		// Bind events
		homeVideoScrollIndicator?.addEventListener('click', scrollToVideoBlock);

		intersectionObserver.observe(document.querySelector('.o-homePageVideo'));

		// Keep track of small/large video visibility breakpoint
		const breakpointMdUp = window.matchMedia(isBreakpointMdUp);
		breakpointMdUp.addEventListener('change', this.watchIsBreakpointMdUp);
	}

	// Handle play/pause between both videos in each block on window resize.
	static watchIsBreakpointMdUp(e) {
		const homePageVideoElement = datajs('home-page-video');

		const largeVideo = datajs('home-page-video', homePageVideoElement);
		const smallVideo = datajs('home-page-video-small', homePageVideoElement);

		// Check if the media query is true
		if (e.matches) {
			// Pause small video when screen resized to isBreakpointMdUp
			if (smallVideo) {
				smallVideo?.plyr.pause();
			}
		} else {
			// Pause large video when a small video exists and screen is resized below isBreakpointMdUp
			if (smallVideo && largeVideo) {
				largeVideo?.plyr.pause();
			}
		}
	}

	static pauseVideos() {
		const homePageVideoElement = datajs('home-page-video');
		const largeVideo = datajs('home-page-video', homePageVideoElement);
		const smallVideo = datajs('home-page-video-small', homePageVideoElement);
		const playControl = datajs('home-video-toggle-play', homePageVideoElement);

		HomePageVideo.videoPlaying = false;
		playControl.removeAttribute('data-playing');
		homePageVideoElement.classList.remove('o-homePageVideo--videoEnabled');
		largeVideo?.plyr.pause();
		smallVideo?.plyr.pause();
	}

	static playVideos() {
		const homePageVideoElement = datajs('home-page-video');
		const largeVideo = datajs('home-page-video', homePageVideoElement);
		const smallVideo = datajs('home-page-video-small', homePageVideoElement);
		const playControl = datajs('home-video-toggle-play', homePageVideoElement);

		const playPauseVideos = function (firstVideo, secondVideo = false) {
			// Pause second video if it exists
			if (secondVideo) {
				secondVideo?.plyr.pause();
			}

			// Play first video if it exists
			if (firstVideo) {
				firstVideo?.plyr.play();
			}
		};

		playControl.setAttribute('data-playing', true);
		homePageVideoElement.classList.add('o-homePageVideo--videoEnabled');

		// Handle multiple video instances (small and large screens)
		if (window.matchMedia(isBreakpointMdUp).matches) {
			playPauseVideos(largeVideo, smallVideo);
		} else {
			// If both a small and a large video exists:,
			if (smallVideo && largeVideo) {
				playPauseVideos(smallVideo, largeVideo);
			}

			// If only small video exists
			else if (smallVideo && !largeVideo) {
				playPauseVideos(smallVideo);
			}

			// If only largeVideo exists
			else if (largeVideo && !smallVideo) {
				playPauseVideos(largeVideo);
			}
		}
	}

	static unmuteVideos() {
		const homePageVideoElement = datajs('home-page-video');
		const largeVideo = datajs('home-page-video', homePageVideoElement);
		const smallVideo = datajs('home-page-video-small', homePageVideoElement);
		const soundControl = datajs('home-video-toggle-sound', homePageVideoElement);

		const unmuteMuteVideos = function (firstVideo, secondVideo = false) {
			// Mute second video if it exists
			if (secondVideo && secondVideo.plyr) {
				secondVideo.plyr.volume = 0;
			}

			// UnMute first video if it exists
			if (firstVideo && firstVideo.plyr) {
				firstVideo.plyr.volume = 1;
			}
		};

		soundControl.setAttribute('data-unmuted', true);

		// Handle multiple video instances (small and large screens)
		if (window.matchMedia(isBreakpointMdUp).matches) {
			unmuteMuteVideos(largeVideo, smallVideo);
		} else {
			// If both a small and a large video exists:,
			if (smallVideo && largeVideo) {
				unmuteMuteVideos(smallVideo, largeVideo);
			}

			// If only small video exists
			else if (smallVideo && !largeVideo) {
				unmuteMuteVideos(smallVideo);
			}

			// If only largeVideo exists
			else if (largeVideo && !smallVideo) {
				unmuteMuteVideos(largeVideo);
			}
		}
	}

	static muteVideos() {
		const homePageVideoElement = datajs('home-page-video');
		const largeVideo = datajs('home-page-video', homePageVideoElement);
		const smallVideo = datajs('home-page-video-small', homePageVideoElement);
		const soundControl = datajs('home-video-toggle-sound', homePageVideoElement);

		soundControl.removeAttribute('data-unmuted');
		HomePageVideo.videoMuted = true;
		if (largeVideo && largeVideo.plyr) {
			largeVideo.plyr.volume = 0;
		}
		if (smallVideo && smallVideo.plyr) {
			smallVideo.plyr.volume = 0;
		}
	}

	// Setup Hero Block
	static setupHomePageVideo(homePageVideoElement) {
		const largeVideo = datajs('home-page-video', homePageVideoElement);
		const smallVideo = datajs('home-page-video-small', homePageVideoElement);
		const soundControl = datajs('home-video-toggle-sound', homePageVideoElement);
		const playControl = datajs('home-video-toggle-play', homePageVideoElement);

		if (largeVideo) {
			this.videoConfig(largeVideo);
		}

		if (smallVideo) {
			this.videoConfig(smallVideo);
		}

		playControl?.addEventListener('click', this.triggerVideoPlayStateChange);
		soundControl?.addEventListener('click', this.triggerVideoSoundStateChange);
	}

	static triggerVideoPlayStateChange() {
		HomePageVideo.videoPlaying = !HomePageVideo.videoPlaying;

		if (HomePageVideo.videoPlaying) {
			HomePageVideo.playVideos();
		} else {
			HomePageVideo.pauseVideos();
		}
	}

	static triggerVideoSoundStateChange() {
		HomePageVideo.videoMuted = !HomePageVideo.videoMuted;

		if (HomePageVideo.videoMuted) {
			HomePageVideo.muteVideos();
		} else {
			HomePageVideo.unmuteVideos();
		}
	}

	// Setup Video
	static videoConfig(
		video,
		config = {
			controls: [],
			clickToPlay: false,
			keyboard: { focused: false, global: false },
		}
	) {
		const player = new Plyr(video, config);

		// Apply same classes to the Plyr wrapper as the initial video element
		if (player.media.classList.contains('d-md-none')) {
			player.elements.container.classList.add('d-md-none');
		}

		if (player.media.classList.contains('d-md-block')) {
			player.elements.container.classList.add('d-md-block');
		}

		if (player.media.classList.contains('d-none')) {
			player.elements.container.classList.add('d-none');
		}

		// Add event listeners to videos to determine when a video has ended
		player.muted = true;
		player.on('ended', HomePageVideo.pauseVideos);
	}
}

HomePageVideo.videoPlaying = false;
HomePageVideo.videoMuted = true;
